import { useCallback, useContext, useEffect, useMemo } from 'react';
import { isEmpty } from 'lodash';
import { globalAction } from 'common/actions';
import { VALUATIONS_VERSION_LABEL } from 'common/constants/valuations';
import { useCompanyBreadcrumb } from 'common/hooks';
import { UseCompanyBreadcrumbItem, UseCompanyBreadcrumbParams } from 'common/hooks/useCompanyBreadcrumb/types';
import { useStore } from 'common/store';
import { LayoutContextValues, UseStoreValues } from 'common/types/store';
import { AllocationStatusBadge } from 'components';
import { ConfirmationDialog } from 'components/Dialogs';
import { ConfirmUnsavedChanges } from 'components/Spreadsheet/components';
import { LayoutContext, useFundInvestmentContext } from 'context';
import { FUND_INVESTMENTS_SAVE_TITLE, FUND_INVESTMENTS_SLUG } from 'pages/FundInvestments/common/constants';
import {
  CHANGE_TO_SCALAR_VALUATION_LABEL,
  GIVE_OWNERSHIP_TO_FIRM_LABEL,
  MAKE_FINAL_ALLOCATION_BUTTON_LABEL,
  PUBLISH_ALLOCATION_BUTTON_LABEL,
  SAVE_AS_NEW_VERSION_LABEL,
} from 'pages/ValuationsAllocation/common/constants/valuations';
import { getNumberValue, getObjectValue, getStringValue } from 'utilities';
import { UseFundInvestmentLayoutParams } from './useFundInvestmentLayout.types';

const useFundInvestmentLayout = (params: UseFundInvestmentLayoutParams) => {
  const { isLoading, onSave, pageTitle, showMarketDateOnFilter, showVersionFilter, validateAllCells } = params;

  const [storeValue, dispatch] = useStore() as UseStoreValues;
  const { user: currentUser } = storeValue;
  const { is_staff: isCurrentUserStaff = false } = getObjectValue(currentUser);

  const { updatePageActions } = useContext(LayoutContext) as LayoutContextValues;

  // Fund investments context
  const {
    fundInvestmentAllocationStatus,
    fundInvestmentAllocationVersion,
    fundInvestmentSpreadsheet,
    fundInvestmentValuationAllocation,
    fundInvestmentValuationOwnership,
    fundInvestmentValuationReadyForAudit,
  } = useFundInvestmentContext();
  const {
    isCurrentAllocationFinal,
    isCurrentAllocationPublished,
    isCurrentValuationDisabled,
    showMakeAllocationFinalDialog,
    showPublishAllocationDialog,
  } = fundInvestmentAllocationStatus;
  const { showNewAllocationVersionDialog } = fundInvestmentAllocationVersion;
  const { arePendingChanges, isValidating, setUnsavedChanges, setAreAllCellsValid } = fundInvestmentSpreadsheet;
  const {
    currentAllocationVersion,
    isCurrentAllocationAccessForbidden,
    isFetchingFundInvestmentByAllocation,
    isFetchingPublicCompsByDate,
    isFetchingValuationByAllocation,
    isLoadingFundInvestmentByAllocation,
    isLoadingValuationByAllocation,
    isUpdatingFundInvestmentDetails,
    isUpdatingValuation,
    setShouldShowMarketDateOnFilter,
    setShouldShowVersionFilter,
    valuationByAllocation,
  } = fundInvestmentValuationAllocation;
  const { isCurrentFirmUserReadOnly, isCurrentUserAuditor, isCurrentValuationFirmOwned, showValuationOwnershipDialog } =
    fundInvestmentValuationOwnership;
  const { isCurrentUserAllowedToToggleReadyForAudit, showValuationReadyForAuditDialog, valuationReadyForAuditLabel } =
    fundInvestmentValuationReadyForAudit;

  const isProcessing = useMemo<boolean>(
    () =>
      isFetchingFundInvestmentByAllocation ||
      isFetchingPublicCompsByDate ||
      isFetchingValuationByAllocation ||
      isLoading ||
      isLoadingFundInvestmentByAllocation ||
      isLoadingValuationByAllocation ||
      isUpdatingFundInvestmentDetails ||
      isUpdatingValuation ||
      isValidating,
    [
      isFetchingFundInvestmentByAllocation,
      isFetchingPublicCompsByDate,
      isFetchingValuationByAllocation,
      isLoading,
      isLoadingFundInvestmentByAllocation,
      isLoadingValuationByAllocation,
      isUpdatingFundInvestmentDetails,
      isUpdatingValuation,
      isValidating,
    ]
  );

  const fundInvestmentsBreadcrumb = useMemo<UseCompanyBreadcrumbParams>(
    () => ({
      page: FUND_INVESTMENTS_SLUG,
      title: pageTitle,
      item: currentAllocationVersion
        ? {
          label: `${VALUATIONS_VERSION_LABEL} ${getNumberValue(
            currentAllocationVersion?.version
          )} - ${getStringValue(currentAllocationVersion?.name)}`,
          status: <AllocationStatusBadge />,
          readyForAudit: currentAllocationVersion?.is_ready_for_audit,
        }
        : ({} as UseCompanyBreadcrumbItem),
    }),
    [currentAllocationVersion, pageTitle]
  );

  // Set company breadcrumb
  useCompanyBreadcrumb(fundInvestmentsBreadcrumb);

  // Unsaved changes dialog
  const showUnsavedChangesDialog = useCallback(
    () =>
      setUnsavedChanges?.({
        content: ConfirmUnsavedChanges,
        wrapper: ConfirmationDialog,
      }),
    [setUnsavedChanges]
  );

  // Display unsaved changes dialog
  useEffect(() => {
    if (arePendingChanges) {
      showUnsavedChangesDialog();
    }
  }, [arePendingChanges, showUnsavedChangesDialog]);

  // Set page actions
  useEffect(() => {
    const shouldDisplaySecondaryActions =
      !isEmpty(valuationByAllocation) &&
      !isCurrentAllocationAccessForbidden &&
      !isCurrentUserAuditor &&
      !isCurrentFirmUserReadOnly;

    const secondaryActions = shouldDisplaySecondaryActions
      ? ([
        // Publish allocation
        {
          callback: showPublishAllocationDialog,
          disabled: arePendingChanges,
          isActive: !isCurrentAllocationPublished && !isCurrentAllocationFinal,
          label: PUBLISH_ALLOCATION_BUTTON_LABEL,
        },
        // Make final allocation
        {
          callback: showMakeAllocationFinalDialog,
          disabled: arePendingChanges,
          isActive: !isCurrentAllocationFinal && isCurrentAllocationPublished,
          label: MAKE_FINAL_ALLOCATION_BUTTON_LABEL,
        },
        // Save as new version
        {
          callback: showNewAllocationVersionDialog,
          isActive: false, // TODO: Re-enable this feature when implementing the save new versions ticket
          label: SAVE_AS_NEW_VERSION_LABEL,
        },
        // Valuation ownership
        {
          label: isCurrentValuationFirmOwned ? CHANGE_TO_SCALAR_VALUATION_LABEL : GIVE_OWNERSHIP_TO_FIRM_LABEL,
          callback: showValuationOwnershipDialog,
          isActive: isCurrentUserStaff,
        },
        // Ready for audit
        {
          label: valuationReadyForAuditLabel,
          callback: showValuationReadyForAuditDialog,
          isActive: isCurrentUserAllowedToToggleReadyForAudit,
        },
      ] as LayoutContextValues['pageActions'])
      : [];

    updatePageActions?.({
      mainAction: {
        callback: onSave,
        isActive: isCurrentUserAuditor || isProcessing || isCurrentValuationDisabled, // The meaning of isActive is inverted here, it should be false when loading
        isLock: isCurrentAllocationFinal,
        label: FUND_INVESTMENTS_SAVE_TITLE,
      },
      secondaryActions,
    });

    // Cleanup when dependencies change or on unmount
    return () => {
      updatePageActions?.(null);
    };
  }, [
    arePendingChanges,
    isCurrentAllocationAccessForbidden,
    isCurrentAllocationFinal,
    isCurrentAllocationPublished,
    isCurrentFirmUserReadOnly,
    isCurrentUserAllowedToToggleReadyForAudit,
    isCurrentUserAuditor,
    isCurrentUserStaff,
    isCurrentValuationDisabled,
    isCurrentValuationFirmOwned,
    isProcessing,
    onSave,
    showMakeAllocationFinalDialog,
    showNewAllocationVersionDialog,
    showPublishAllocationDialog,
    showValuationOwnershipDialog,
    showValuationReadyForAuditDialog,
    updatePageActions,
    valuationByAllocation,
    valuationReadyForAuditLabel,
  ]);

  // Version filter
  useEffect(() => {
    setShouldShowVersionFilter(showVersionFilter);
  }, [setShouldShowVersionFilter, showVersionFilter]);

  // Market date on filter
  useEffect(() => {
    setShouldShowMarketDateOnFilter(showMarketDateOnFilter);
  }, [setShouldShowMarketDateOnFilter, showMarketDateOnFilter]);

  // Cell validation
  useEffect(() => {
    setAreAllCellsValid(validateAllCells);
  }, [setAreAllCellsValid, validateAllCells]);

  // Show loading progress bar
  useEffect(() => {
    dispatch?.(globalAction.showLoadingProgress(isProcessing));

    // Cleanup when dependencies change or on unmount
    return () => {
      dispatch?.(globalAction.showLoadingProgress(false));
    };
  }, [dispatch, isProcessing]);
};

export default useFundInvestmentLayout;
